import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeCanvas } from 'qrcode.react';
import { Button, ITab, Select, Tabs } from '../../components';
import { useSelector } from '../../redux/store';
import { getAccount, getIsMattressExpert } from '../../redux/selectors';

const REGISTER_PATH = '/register';

const LEVELS = ['L', 'M', 'Q', 'H'] as const;

const logoSizes = {
  L: 156,
  M: 156,
  Q: 256,
  H: 256,
};

export const PatientRegistrationQR: FC = () => {
  const { t } = useTranslation();
  const account = useSelector(getAccount);
  const isMattressExpert = useSelector(getIsMattressExpert);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasInsurance, setHasInsurance] = useState(false);
  const [includeLogo, setIncludeLogo] = useState(true);
  const [level, setLevel] = useState<'L' | 'M' | 'Q' | 'H'>('M');

  const logoUrl = hasInsurance
    ? '/assets/images/logos/back-health-square.png'
    : '/assets/images/logos/physioscan-square.png';

  const registerUrl = useMemo(() => {
    if (!account?.id) return '';
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const params = new URLSearchParams({ doctorId: account.id, hasInsurance: String(hasInsurance) });
    return `${base}${REGISTER_PATH}?${params.toString()}`;
  }, [account?.id, hasInsurance]);

  const imageSettings = useMemo(() => {
    if (!includeLogo) return undefined;
    return {
      src: logoUrl,
      height: logoSizes[level],
      width: logoSizes[level],
      excavate: true,
    };
  }, [includeLogo, logoUrl, level]);

  const tabs = useMemo<ITab[]>(
    () => [
      {
        value: false,
        label: <img className="h-8" src="/assets/images/logos/physioscan.png" alt="PhysioScan" />,
      },
      {
        value: true,
        label: <img className="h-8" src="/assets/images/logos/back-health.png" alt="BackHealth" />,
      },
    ],
    [],
  );

  const onDownload = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !registerUrl) return;
    const link = document.createElement('a');
    link.download = `patient-registration-qr-${hasInsurance ? 'backhealth' : 'physioscan'}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [registerUrl, hasInsurance]);

  if (!account?.id) return null;

  return (
    <div className="flex flex-col gap-6 md:flex-row">
      <div className="flex max-w-140 flex-col gap-4">
        <p className="text-gray-dark">{t('patientRegistrationQR.description')}</p>
        {!isMattressExpert && (
          <Tabs
            className="w-full"
            tabs={tabs}
            value={hasInsurance}
            fullWidth
            onChange={(value) => setHasInsurance(value as boolean)}
          />
        )}

        <div className="flex w-full flex-col gap-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="qr-include-logo"
              checked={includeLogo}
              onChange={(e) => setIncludeLogo(e.target.checked)}
              className="h-4 w-4 rounded border-stroke3"
            />
            <label htmlFor="qr-include-logo" className="text-sm text-typo1">
              {t('patientRegistrationQR.includeLogo')}
            </label>
          </div>
          <Select<'L' | 'M' | 'Q' | 'H'>
            fullWidth
            size="sm"
            label={t('patientRegistrationQR.level')}
            value={level}
            options={[...LEVELS]}
            getOptionLabel={(o) => o}
            onChange={setLevel}
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col items-center gap-4">
        <div className="flex w-full justify-end">
          <Button variant="outline" typoClass="typo-button-lg uppercase" onClick={onDownload}>
            {t('patientRegistrationQR.download')}
          </Button>
        </div>
        <div className="flex aspect-square flex-col items-center rounded-2xl border border-stroke3 bg-white p-2">
          <QRCodeCanvas
            className="h-full max-h-140 w-full max-w-140"
            ref={canvasRef}
            value={registerUrl}
            size={1024}
            level={level}
            marginSize={1}
            imageSettings={imageSettings}
          />
        </div>
      </div>
    </div>
  );
};
